import { UseFormReturn } from 'react-hook-form';
import {
    IDictFilter,
    IDictFilters,
    IDocumentTable,
    IFormValues,
    ISetValue,
    ITableColumn,
    ITableColumnAbook,
    ITableColumnAutoComplete,
    ITableColumnCalc,
    ITableColumnDict,
    ITableColumnUrl,
} from '@models/Forms/IForms';
import { getFieldKeysByChanges, parseDisplayFormatCell } from '@utils/documentUtils';
import './EditTable.scss';
import { IField, IFieldElem } from '@models/IFormData';
import { FormulaManager } from '@utils/managers/FormulaManager';
import React, { FocusEvent, useCallback, useEffect, useMemo, useState } from 'react';
import Dictpicker from '@atoms/Dictpicker';
import { IDictionaryData } from '@models/dictionary/IDictionaryData';
import Abookpicker from '@atoms/Abookpicker';
import { IAddressBookData } from '@models/addressbook/IAddressBookData';
import Checkbox from '@atoms/Checkbox';
import Textarea from '@atoms/Textarea';
import InputNumber from '@atoms/InputNumber/InputNumber';
import Datepicker from '@atoms/Datepicker';
import Input from '@atoms/Input';
import DisplayField from '@atoms/DisplayField/DisplayField';
import TableControl from '@controls/TableControl';
import { ChangesFieldValue, GetFormulaValues, GetValueForSetValue } from '@utils/managers/ChangesManager';
import Select from '@atoms/Select';
import { IOption } from '@/types';
import { DictExternalDataSource } from '@utils/DictExternalDataSource';
import { ValueType } from '@/types/ValueType';
import { getOptions } from '@molecules/formbuilder/controls/AutoComplete/AutoCompleteHelper';
import AutoCompletePicker from '@atoms/AutoCompletePicker/AutoCompletePicker';
import {
    cellTextValueSwitcher,
    isTableColumn,
    isTableColumnAbook,
    isTableColumnAutoComplete,
    isTableColumnCalc,
    isTableColumnDict,
    isTableColumnUrl,
} from '@utils/tableHelper';
import Moment from 'moment';
import { round } from 'mathjs';
import { checkGridRowsByScript } from '@utils/devextreme/dataGridUtils';
import clsx from 'clsx';
import { setValueTyped } from '@utils/helpers';
import { SetConditionManager } from '@utils/managers/SetConditionManager';

export interface IEditTableProps<TFieldValues extends object = object> {
    table?: IDocumentTable;
    formMethods: UseFormReturn<TFieldValues>;
    setError: (errors?: string[]) => void;
    isEdit: boolean;
    isNew: boolean;
    turnOffInvokeChangesOnNew: boolean;
    fields: Record<string, IFieldElem>;
    docId?: string;
}

const EditTable = <TFieldValues extends object = object>({
    table,
    formMethods,
    setError,
    isEdit,
    isNew,
    turnOffInvokeChangesOnNew,
    fields,
    docId,
    ...props
}: IEditTableProps<TFieldValues>) => {
    const approvalListModeRules = table?.approvalListMode;
    const approvalListModeMng = useMemo(() => {
        const manager = new FormulaManager(approvalListModeRules!);
        manager.Init(fields, formMethods);
        return manager;
    }, [approvalListModeRules, fields, formMethods]);

    const activated = React.useRef(false);
    const dataField = fields[table?.key!];

    const [approvalListMode, setApprovalListMode] = useState<boolean>(false);
    const InitFormulas = async () => {
        if (approvalListModeRules) {
            let appMode = await approvalListModeMng.EvalFormulaValues(isEdit, isNew);
            if (activated.current) {
                setApprovalListMode(appMode);
            }
        }
        if (isNew && table?.invokeChangesOnNew && !turnOffInvokeChangesOnNew) {
            await onTableChange(dataField.value, table);
        }
    };

    useEffect(() => {
        activated.current = true;
        InitFormulas();
        return () => {
            activated.current = false;
        };
    }, []);

    useEffect(() => {
        if (isEdit && table?.invokeChangesOnEdit) {
            onTableChange(dataField.value, table);
        }
    }, [isEdit]);

    const onSaveFieldWithOutUpdate = async (item: ISetValue, table?: IDocumentTable, rowData?: any, value?: any) => {
        let val = value ? value : await GetValueForSetValue(item, table?.key, fields, rowData, formMethods);
        if (item.key.includes('|Document')) {
            let field = fields[item.key];
            if (field === undefined) throw new Error(`Поле ${item.key} не найдено в перечне полей`);
            formMethods.setValue(field.name as any, val, { shouldDirty: true });
        } else {
            const column = table?.tableColumn.find((col) => {
                return col.key == item.key;
            });
            if (
                column &&
                (column.valueType == ValueType.Date ||
                    column.valueType == ValueType.DateTime ||
                    column.valueType == ValueType.NoSecDateTime)
            ) {
                if (Moment(val, 'DD.MM.YYYY').isValid()) {
                    val = Moment(val, 'DD.MM.YYYY').toDate();
                } else {
                    if (Moment(val).isValid()) {
                        val = Moment(val).toDate();
                    }
                }
                rowData[item.key] = new Date(val);
            } else {
                rowData[item.key] = val;
            }
        }
    };
    const onSaveField = async (
        item: ISetValue,
        table?: IDocumentTable,
        rowData?: any,
        value?: any,
        withoutUpdateForm?: boolean,
    ) => {
        await onSaveFieldWithOutUpdate(item, table, rowData, value);
        if (table && (withoutUpdateForm === undefined || !withoutUpdateForm)) {
            const field = fields[table.key];
            if (field) {
                const tableRows = formMethods.getValues(field.name as any);
                const rowsArray = Array.isArray(tableRows) ? (tableRows as any[]) : [];
                const result = rowsArray.map((o: any) => (rowData['|NUM'] === o['|NUM'] ? rowData : o));
                formMethods.setValue(field?.name as any, result as any, { shouldDirty: false });
            }
        }
    };

    const getValue = (name: string, rowData?: any, rowParent?: any) => {
        if (name.includes('|Document') || name.startsWith('#links')) {
            let field = fields[name];
            let fname = ('fields.[' + field.index + '].value') as any;
            let val = formMethods.getValues(fname);
            return val as any;
        } else if (name.includes('|Parent') && rowParent) {
            let fname = name.replace('|Parent', '');
            type ObjectKey = keyof typeof rowParent;
            const attrNAme = fname as ObjectKey;
            return rowParent[attrNAme] as any;
        } else {
            if (rowData) {
                type ObjectKey = keyof typeof rowData;
                const attrNAme = name as ObjectKey;
                return rowData[attrNAme] as any;
            }
        }
    };

    const evalTableFormulaValue = async (condition: string, rowData?: any, rowParent?: any) => {
        let conditionMng = new FormulaManager(condition);
        conditionMng.Init(fields!, formMethods, rowParent);
        let coll = conditionMng.GetFields();
        // var common = fields.filter((x) => keysNew.indexOf(x) !== -1);
        // if (common.length > 0) {
        let values: any[] | undefined = [];
        coll.forEach((f) => {
            if (f.includes('|Document') || f.startsWith('#links')) {
                let field = fields[f];
                let fname = ('fields.[' + field.index + '].value') as any;
                let val = formMethods.getValues(fname);
                values?.push(val as any);
            } else if (f.includes('|Parent') && rowParent) {
                let fname = f.replace('|Parent', '');
                type ObjectKey = keyof typeof rowParent;
                const attrNAme = fname as ObjectKey;
                values?.push(rowParent[attrNAme] as any);
            } else {
                if (rowData) {
                    type ObjectKey = keyof typeof rowData;
                    const attrNAme = f as ObjectKey;
                    values?.push(rowData[attrNAme] as any);
                }
            }
        });
        if (values.every((element) => element === undefined)) {
            values = undefined;
        }

        return await conditionMng.EvalFormulaValues(isEdit, isNew, values);
    };

    const getFormValuesAsync = async (formValues: IFormValues, row: any) => {
        let formDataParams = formValues;
        const map = {} as Record<string, any>;
        if (formDataParams) {
            for (const ele of formDataParams.formValue) {
                if (ele.attr?.includes('|Document')) {
                    if (ele.formula) {
                        let manager = new FormulaManager(ele.formula);
                        manager.Init(fields, formMethods);
                        map[ele.key] = await manager.ReplaceFormulaValues(isEdit, isNew);
                    } else {
                        let field = fields[ele.attr];
                        if (field === undefined)
                            throw new Error(`Поле ${ele.attr} не найдено в перечне полей formValues`);
                        let fname = ('fields.[' + field.index + '].value') as any;
                        let val = formMethods.getValues(fname);
                        if (ele.column) {
                            if (ele.function === '{join}') {
                                map[ele.key] =
                                    (val as [])?.map((item: any) => `'${item[ele.column]}'`).join(',') ?? val;
                            } else {
                                map[ele.key] = (val as [])?.map((item: any) => item[ele.column]) ?? val;
                            }
                        } else {
                            map[ele.key] = val;
                        }
                    }
                } else {
                    if (ele.formula) {
                        let manager = new FormulaManager(ele.formula);
                        manager.Init(fields, formMethods);
                        let _fields = manager.GetFields();
                        let values = _fields.map((field) => {
                            return getValue(field, row);
                        });
                        map[ele.key] = await manager.ReplaceFormulaValues(isEdit, isNew, values);
                    } else {
                        map[ele.key] = getValue(ele.attr, row);
                    }
                }
            }
        }
        return JSON.stringify(map);
    };

    const getFormValues = (formValues: IFormValues, row: any) => {
        let formDataParams = formValues;

        const map = {} as Record<string, any>;
        if (formDataParams) {
            for (const ele of formDataParams.formValue) {
                if (ele.attr.includes('|Document')) {
                    let field = fields[ele.attr];
                    if (field === undefined) throw new Error(`Поле ${ele.attr} не найдено в перечне полей formValues`);
                    let fname = ('fields.[' + field.index + '].value') as any;
                    let val = formMethods.getValues(fname);
                    if (ele.column) {
                        if (ele.function === '{join}') {
                            map[ele.key] = (val as [])?.map((item: any) => `'${item[ele.column]}'`).join(',') ?? val;
                        } else {
                            map[ele.key] = (val as [])?.map((item: any) => item[ele.column]) ?? val;
                        }
                    } else {
                        map[ele.key] = val;
                    }
                } else {
                    type ObjectKey = keyof typeof row;
                    const attrNAme = ele.attr as ObjectKey;
                    map[ele.key] = row[attrNAme] as any;
                }
            }
        }
        return JSON.stringify(map);
    };

    const getWatchesByFormula = (formulas?: string[], rowParent?: any) => {
        let result: string[] = [];
        if (formulas) {
            for (let index = 0; index < formulas.length; index++) {
                const formula = formulas[index];
                if (formula) {
                    let vis = new FormulaManager(formula);
                    vis.Init(fields, formMethods, rowParent);
                    let data = vis.GetWatchFields();
                    data.forEach((item: string) => {
                        result.push(item);
                    });
                }
            }
        }

        return result;
    };

    const getColumnWatches = (table?: IDocumentTable, rowParent?: any) => {
        let result: string[] = [];
        let allColumns: any[] = [];
        allColumns = allColumns
            .concat(table?.tableColumn)
            .concat(table?.tableColumnAbook)
            .concat(table?.tableColumnCalc)
            .concat(table?.tableColumnDict);
        allColumns.forEach((col) => {
            if (col) {
                if (col.visibilityRules != undefined && col.visibilityRules != null) {
                    let vis = new FormulaManager(col.visibilityRules);
                    vis.Init(fields, formMethods, rowParent);
                    result = result.concat(vis.GetWatchFields());
                }
                if (col.required != undefined && col.required != null) {
                    let vis = new FormulaManager(col.required);
                    vis.Init(fields, formMethods, rowParent);
                    result = result.concat(vis.GetWatchFields());
                }
                if (col.readonly != undefined && col.readonly != null) {
                    let vis = new FormulaManager(col.readonly);
                    vis.Init(fields, formMethods, rowParent);
                    result = result.concat(vis.GetWatchFields());
                }
            }
        });
        return result;
    };

    const getFiltersAsync = async (dictFilters: IDictFilters, row: any) => {
        let filters = dictFilters;
        let result = {} as IDictFilter;
        if (filters) {
            for (const ele of filters.filter) {
                let conditionMng = new FormulaManager(ele.condition);
                conditionMng.Init(fields, formMethods);
                let coll = conditionMng.GetFields();
                let values: any[] = [];
                coll.forEach((f) => {
                    if (f.includes('|Document')) {
                        let field = fields[f];
                        let val = formMethods.getValues(field.name as any);
                        values.push(val);
                    } else {
                        type ObjectKey = keyof typeof row;
                        const attrNAme = f as ObjectKey;
                        values.push(row[attrNAme] as any);
                    }
                });
                if (await conditionMng.EvalFormulaValues(isEdit, isNew, values)) {
                    result.condition = ele.condition;
                    if (ele.filter) {
                        let filterMng = new FormulaManager(ele.filter);
                        filterMng.Init(fields, formMethods);
                        result.filter = (await filterMng.ReplaceFormulaValues(isEdit, isNew))!;
                    }
                    if (ele.script) {
                        let scriptMng = new FormulaManager(ele.script);
                        scriptMng.Init(fields, formMethods);
                        result.script = (await scriptMng.ReplaceFormulaValues(isEdit, isNew))!;
                    }
                }
            }
        }
        return result;
    };

    const getValues = (values: IDictionaryData[], attr: string) => {
        let result = [] as string[];
        values.forEach((item) => {
            if (attr == 'code') {
                result.push(item.code);
            } else {
                let elem = item.fields.find((obj) => {
                    return obj.name === attr;
                });
                result.push(elem?.value?.toString()!);
            }
        });
        return result.join('|');
    };

    const getValuesAbook = (values: IAddressBookData[], attr: string) => {
        let result = [] as string[];
        values.forEach((item) => {
            type ObjectKey = keyof typeof item;
            const attrNAme = attr as ObjectKey;
            result.push(item[attrNAme]?.toString());
        });
        return result.join('|');
    };

    const getExternalDataSource = (dict: ITableColumnDict) => {
        const dictExternalDataSource = new DictExternalDataSource(dict.externalDataSource);
        return dictExternalDataSource.GetData(formMethods, fields);
    };

    const getParentFields = () => {
        let fields: IField[] = [];
        let values = formMethods.getValues() as any;
        let coll = values.fields as IField[];
        if (coll != undefined) {
            for (let index = 0; index < coll.length; index++) {
                const field_src = coll[index];
                //if (field_src.name != table?.key) {
                let objCopy = { ...field_src };
                fields.push(objCopy);
                //}
            }
        }
        return fields;
    };

    const renderColumnGrid = async (column: ITableColumn, p: any, rowParent?: any) => {
        const readOnly = column.readonly
            ? column.readonly == 'true'
                ? true
                : await evalTableFormulaValue(column.readonly, p.data, rowParent)
            : false;

        const workCalendar = column.workCalendarRules
            ? column.workCalendarRules == 'true'
                ? true
                : await evalTableFormulaValue(column.workCalendarRules, p.data, rowParent)
            : false;

        const options: IOption[] = [];
        let countItems = 100;
        let startYear = 1950;

        if (column?.min && Number(column?.min) > 1950) {
            startYear = Number(column.min);
        }
        if (column?.max && column.max) {
            countItems = Number(column.max) - startYear;
        }

        if (column?.valueType == ValueType.Year) {
            for (let index = 0; index <= countItems; index++) {
                const year = startYear + index;
                options.push({
                    label: year.toString(),
                    value: year.toString(),
                } as IOption);
            }
        }

        const minVal = await evalTableFormulaValue(column.min, p.data, rowParent);
        const maxVal = await evalTableFormulaValue(column.max!, p.data, rowParent);

        switch (column?.valueType) {
            case ValueType.Boolean:
                return (
                    <Checkbox
                        readOnly={readOnly}
                        disabled={readOnly}
                        name={column.key}
                        defaultChecked={p.value && (p.value === 1 || p.value === '1') ? true : false}
                        onChange={(e) => {
                            let val = e.currentTarget.checked ? 1 : 0;
                            p.setValue(val);
                            p.component.saveEditData();
                        }}
                    />
                );
            case ValueType.Year:
                return (
                    <Select
                        disabled={readOnly}
                        readOnly={true}
                        options={options}
                        onlySelect={true}
                        values={
                            p.value
                                ? [
                                      {
                                          value: p.value.toString(),
                                          label: p.value.toString(),
                                      } as IOption,
                                  ]
                                : undefined
                        }
                        onChange={(option: IOption[]) => {
                            if (option.length > 0) {
                                let val = option[0].value;
                                if (p.value === undefined || p.value === null || p.value.toString() != val.toString()) {
                                    p.setValue(+val);
                                }
                            }
                        }}
                        placeholder={column?.placeholder ? column.placeholder : undefined}
                    />
                );
            case ValueType.YesNo:
                return (
                    <Select
                        disabled={readOnly}
                        readOnly={true}
                        options={[
                            {
                                value: '1',
                                label: column.trueText ?? 'Да',
                            },
                            {
                                value: '0',
                                label: column.falseText ?? 'Нет',
                            },
                        ]}
                        onlySelect={true}
                        values={[
                            {
                                value: p.value?.toString() ?? '',
                                label:
                                    p.value?.toString() == '1'
                                        ? (column.trueText ?? 'Да')
                                        : p.value?.toString() == '0'
                                          ? (column.falseText ?? 'Нет')
                                          : '',
                            } as IOption,
                        ]}
                        onChange={(option: IOption[]) => {
                            if (option.length > 0) {
                                let val = option[0].value == '1' ? 1 : 0;
                                if (p.value === undefined || p.value === null || p.value.toString() != val.toString()) {
                                    p.setValue(val);
                                }
                            }
                        }}
                        placeholder={column?.placeholder ? column.placeholder : undefined}
                    />
                );
            case ValueType.LongText:
                return (
                    <Textarea
                        autoResize={true}
                        readOnly={readOnly}
                        name={column.key}
                        initialRowCount={1}
                        value={p.value}
                        onValueChange={(e) => {
                            p.setValue(e);
                        }}
                        placeholder={column?.placeholder ? column.placeholder : undefined}
                        maxLength={column?.maxLength}
                        autoFocus={false}
                        onFocus={onFocusTextElement}
                    />
                );
            case ValueType.Double:
                return (
                    <InputNumber
                        readOnly={readOnly}
                        name={column.key}
                        groupBy={0}
                        separator={''}
                        floatPoints={column.floatPoints ?? 2}
                        defaultValue={p.value}
                        onInputChange={(value) => {
                            let val = +value.replace(/\s/g, '');
                            if (p.value != val) {
                                p.setValue(val);
                            }
                        }}
                        placeholder={column?.placeholder ? column.placeholder : undefined}
                        autoFocus={false}
                        onFocus={onFocusTextElement}
                    />
                );
            case ValueType.Money:
                return (
                    <InputNumber
                        readOnly={readOnly}
                        name={column.key}
                        floatPoints={column.floatPoints ?? 2}
                        defaultValue={p.value}
                        onInputChange={(value) => {
                            let val = +value.replace(/\s/g, '');
                            if (p.value != val) {
                                p.setValue(val);
                            }
                        }}
                        placeholder={column?.placeholder ? column.placeholder : undefined}
                        autoFocus={false}
                        onFocus={onFocusTextElement}
                    />
                );
            case ValueType.Integer:
                return (
                    <InputNumber
                        readOnly={readOnly}
                        name={column.key}
                        groupBy={0}
                        separator={''}
                        defaultValue={p.value}
                        onInputChange={(value) => {
                            let val = +value;
                            if (p.value != val) {
                                p.setValue(val);
                            }
                        }}
                        placeholder={column?.placeholder ? column.placeholder : undefined}
                        autoFocus={false}
                        onFocus={onFocusTextElement}
                    />
                );
            case ValueType.NoSecDateTime:
            case ValueType.Date:
            case ValueType.DateTime:
                return (
                    <Datepicker
                        readOnly={readOnly}
                        name={column.key}
                        defaultValue={p.value}
                        minDate={minVal}
                        maxDate={maxVal}
                        workCalendar={workCalendar}
                        onChange={(e) => {
                            const val = e.date.value ?? null;
                            p.setValue(val);
                        }}
                        placeholder={column?.placeholder ? column.placeholder : undefined}
                        autoFocus={false}
                        toggleOnFocus={false}
                    />
                );

            case ValueType.Url:
                return (
                    <Input
                        pattern={column.inputRegExp}
                        readOnly={readOnly}
                        name={column.key}
                        defaultValue={p.value}
                        onChange={(e) => {
                            p.setValue(e.currentTarget.value);
                        }}
                        autoFocus={false}
                        onFocus={onFocusTextElement}
                    />
                );

            case ValueType.Text:
                return (
                    <Input
                        maxLength={255}
                        pattern={column.inputRegExp}
                        readOnly={readOnly}
                        name={column.key}
                        defaultValue={p.value}
                        onChange={(e) => {
                            p.setValue(e.currentTarget.value);
                        }}
                        placeholder={column?.placeholder ? column.placeholder : undefined}
                        autoFocus={false}
                        onFocus={onFocusTextElement}
                    />
                );

            default:
                return <div />;
        }
    };

    const renderColumnDictGrid = async (column: ITableColumnDict, p: any, rowParent?: any) => {
        let readOnly = column.readonly
            ? column.readonly == 'true'
                ? true
                : await evalTableFormulaValue(column.readonly, p.data, rowParent)
            : false;
        return (
            <Dictpicker
                readOnly={readOnly}
                docId={docId}
                isSelectMode={column.isSelectMode}
                dictionaryName={column.dictName}
                modalTitle={column.modalTitle}
                isFormData={column.isFormData}
                isMultiple={column.isMultiple}
                selectableLevels={column.selectableLevels}
                visibleLevels={column.visibleLevels}
                predicatesCache={column.predicatesCache}
                gridAttribute={column.gridAttribute}
                externalSearch={column.externalSearch}
                addForm={column.addForm}
                loadMode={column.loadMode}
                getExternalDataSource={column?.externalDataSource ? () => getExternalDataSource(column) : undefined}
                getFormValuesAsync={async () => {
                    return await getFormValuesAsync(column.formValues, p.data);
                }}
                getFiltersAsync={async () => {
                    return await getFiltersAsync(column.filters, p.data);
                }}
                defaultValue={p.value}
                displayValue={parseDisplayFormatCell(column.displayFormat, p.data)}
                insideGrid={true}
                autoFocus={false}
                source={column.source}
                useClientSideDataProcessing={column.useClientSideDataProcessing}
                placeholder={column?.placeholder ? column.placeholder : undefined}
                userScripts={column.userScripts}
                wordWrapEnabled={column?.wordWrapEnabled}
                onChange={async (e) => {
                    if (e && e.length > 0) {
                        if (column?.setValues?.sets) {
                            await Promise.all(
                                column.setValues.sets.map(async (el) => {
                                    let val: any = undefined;
                                    if (el.attr === 'empty') {
                                        val = p.data[el.key] === '' ? undefined : '';
                                    } else {
                                        val = getValues(e, el.attr);
                                        val = setValueTyped(val, el);
                                    }

                                    const conditionManager = new SetConditionManager(
                                        val,
                                        fields,
                                        formMethods,
                                        isNew,
                                        isEdit,
                                    );
                                    const conditionResult = await conditionManager.checkConditionAsync(el.setCondition);
                                    if (conditionResult) {
                                        p.data[el.key] = val;
                                    }
                                }),
                            );
                        }
                        //p.data[column.key] = getValues(e, 'code');

                        p.setValue(getValues(e, 'code'));
                    } else {
                        column.setValues?.sets?.forEach((el) => {
                            p.data[el.key] = '';
                        });
                        //p.data[column.key] = '';
                        p.setValue('');
                    }
                    // p.component.cancelEditData();
                    p.component.saveEditData();
                }}
                onCancel={() => {
                    p.component.cancelEditData();
                }}
            />
        );
    };

    const renderColumnAutoCompleteGrid = async (column: ITableColumnAutoComplete, p: any, rowParent?: any) => {
        let readOnly = column.readonly
            ? column.readonly == 'true'
                ? true
                : await evalTableFormulaValue(column.readonly, p.data, rowParent)
            : false;
        return (
            <AutoCompletePicker
                disabled={readOnly}
                defInputValue={p.value}
                debounceTime={500}
                placeholder={column?.placeholder ? column.placeholder : undefined}
                findOptions={async (text: string) => {
                    return await getOptions(column?.dataSource!, text);
                }}
                onInputValueChanged={(e) => {
                    p.setValue(e);
                }}
            />
        );
    };

    const renderColumnCalcGrid = async (column: ITableColumnCalc, p: any, rowParent?: any) => {
        let readOnly = column.readonly
            ? column.readonly == 'true'
                ? true
                : await evalTableFormulaValue(column.readonly, p.data, rowParent)
            : false;
        const typeValue = column.valueType !== undefined ? column.valueType : ValueType.Integer;
        switch (typeValue) {
            case ValueType.Double:
                return (
                    <InputNumber
                        readOnly={readOnly}
                        name={column.key}
                        groupBy={0}
                        separator={''}
                        floatPoints={column.floatPoints ?? 2}
                        defaultValue={p.value}
                        onInputChange={(value) => {
                            let val = +value.replace(/\s/g, '');
                            if (p.value != val) {
                                p.setValue(val);
                            }
                        }}
                        placeholder={column?.placeholder ? column.placeholder : undefined}
                        autoFocus={false}
                        onFocus={onFocusTextElement}
                    />
                );
            case ValueType.Money:
                return (
                    <InputNumber
                        readOnly={readOnly}
                        name={column.key}
                        floatPoints={column.floatPoints ?? 2}
                        defaultValue={p.value}
                        onInputChange={(value) => {
                            let val = +value.replace(/\s/g, '');
                            if (p.value != val) {
                                p.setValue(val);
                            }
                        }}
                        placeholder={column?.placeholder ? column.placeholder : undefined}
                        autoFocus={false}
                        onFocus={onFocusTextElement}
                    />
                );
            default:
                return (
                    <InputNumber
                        readOnly={readOnly}
                        name={column.key}
                        groupBy={0}
                        separator={''}
                        defaultValue={p.value}
                        onInputChange={(value) => {
                            let val = +value;
                            if (p.value != val) {
                                p.setValue(val);
                            }
                        }}
                        placeholder={column?.placeholder ? column.placeholder : undefined}
                        autoFocus={false}
                        onFocus={onFocusTextElement}
                    />
                );
        }
    };
    const renderColumnAbookGrid = async (column: ITableColumnAbook, p: any, rowParent?: any) => {
        let readOnly = column.readonly
            ? column.readonly == 'true'
                ? true
                : await evalTableFormulaValue(column.readonly, p.data, rowParent)
            : false;
        return (
            <Abookpicker
                readOnly={readOnly}
                insideGrid={true}
                autoFocus={false}
                isMultiple={column.isMultiple}
                tabsSettings={column.tabs}
                externalSearch={column.externalSearch}
                formValues={getFormValues(column.formValues, p.data)}
                title={column.name}
                defaultValue={p.value}
                showChips={column.showChips}
                wordWrapEnabled={column.wordWrapEnabled}
                displayValue={parseDisplayFormatCell(column.displayFormat, p.data)}
                placeholder={column?.placeholder ? column.placeholder : undefined}
                onChange={async (e) => {
                    if (e && e.length > 0) {
                        if (column?.setValues?.sets) {
                            await Promise.all(
                                column.setValues.sets.map(async (el) => {
                                    if (el.attr === 'empty') {
                                        p.data[el.key] = p.data[el.key] === '' ? undefined : '';
                                    } else {
                                        const val = getValuesAbook(e, el.attr);
                                        const conditionManager = new SetConditionManager(
                                            val,
                                            fields,
                                            formMethods,
                                            isNew,
                                            isEdit,
                                        );
                                        const conditionResult = await conditionManager.checkConditionAsync(
                                            el.setCondition,
                                        );
                                        if (conditionResult) {
                                            p.data[el.key] = val;
                                        }
                                    }
                                }),
                            );
                        }
                        p.setValue(getValuesAbook(e, 'key'));
                    } else {
                        column.setValues.sets.forEach((el) => {
                            p.data[el.key] = '';
                        });
                        p.setValue('');
                    }
                    p.component.saveEditData();
                    p.component.cancelEditData();
                }}
            />
        );
    };

    const renderColumnUrlGrid = async (column: ITableColumnUrl, p: any, rowParent?: any) => {
        return (
            <Input
                readOnly={true}
                name={column.key}
                defaultValue={p.value}
                onChange={(e) => {
                    p.setValue(e.currentTarget.value);
                }}
                autoFocus={false}
                onFocus={onFocusTextElement}
            />
        );
    };

    const cellRenderSwitcherField = async (p: any, column: any, rowParent?: any) => {
        let content = cellRenderSwitcher(p, column);
        let readOnly = column.readonly
            ? column.readonly === 'true'
                ? true
                : await evalTableFormulaValue(column.readonly, p.data, rowParent)
            : false;
        let required = column.required
            ? column.required === 'true'
                ? true
                : await evalTableFormulaValue(column.required, p.data, rowParent)
            : false;

        let regexp = column.pattern ? new RegExp(column.pattern, 'g') : null;
        let regval = p.value === undefined || p.value === null ? '' : p.value;
        const invalidCell =
            (required && (p.value === undefined || p.value === null || p.value === '')) ||
            (regexp && !regexp.test(regval)) ||
            (p.value && column.max && p.value.length > column.max) ||
            (column.min && p.value !== undefined && p.value !== null && p.value !== '' && p.data.length < column.min);
        let cellClassName = clsx('table-cell', {
            'invalid-cell': invalidCell,
        });
        if (column.styles) {
            for (let index = 0; index < column.styles.style.length; index++) {
                const x = column.styles.style[index];
                let result = await evalTableFormulaValue(x.condition, p.data, rowParent);
                if (result) {
                    cellClassName += ' ' + x.cellStyle;
                }
            }
        }

        let className =
            column.valueType === ValueType.LongText || column.valueType === ValueType.Base64Text
                ? 'longtext-cell'
                : 'content-cell';
        className = column.valueType === ValueType.Text ? 'text-cell' : className;

        return (
            <div className={cellClassName}>
                <div
                    className={clsx('w100', className, {
                        'readonly-cell': readOnly,
                    })}
                >
                    {content}
                </div>
            </div>
        );
    };

    const cellRenderSwitcher = (p: any, column: any) => {
        if (isTableColumnDict(column) || isTableColumnAbook(column)) {
            return <div>{parseDisplayFormatCell(column.displayFormat, p.data)}</div>;
        } else if (isTableColumnCalc(column)) {
            const typeValue = column.valueType !== undefined ? column.valueType : ValueType.Integer;
            return <DisplayField type={typeValue} value={p.value} />;
        } else if (isTableColumnAutoComplete(column)) {
            return <DisplayField type={ValueType.Text} value={p.value} />;
        } else if (isTableColumnUrl(column)) {
            const value = parseDisplayFormatCell(column.displayFormat, p.data);
            const href = parseDisplayFormatCell(column.href, p.data);
            return <DisplayField type={ValueType.Url} value={value} href={href} />;
        } else if (isTableColumn(column)) {
            return (
                <DisplayField
                    type={column?.valueType}
                    value={p.value}
                    trueText={column?.trueText}
                    falseText={column?.falseText}
                />
            );
        }
    };

    const editCellRenderSwitcher = async (p: any, column: any, rowParent?: any) => {
        if (isTableColumnCalc(column)) {
            return await renderColumnCalcGrid(column, p, rowParent);
        } else if (isTableColumnAbook(column)) {
            return await renderColumnAbookGrid(column, p, rowParent);
        } else if (isTableColumnDict(column)) {
            return await renderColumnDictGrid(column, p, rowParent);
        } else if (isTableColumnAutoComplete(column)) {
            return await renderColumnAutoCompleteGrid(column, p, rowParent);
        } else if (isTableColumnUrl(column)) {
            return await renderColumnUrlGrid(column, p, rowParent);
        } else if (isTableColumn(column)) {
            return await renderColumnGrid(column, p, rowParent);
        }
    };

    const calculateRow = async (row: any, column: any, table: IDocumentTable) => {
        if (table.tableColumnCalc) {
            for (let index = 0; index < table.tableColumnCalc.length; index++) {
                let keysChanged: string[] = [];
                const item = table.tableColumnCalc[index];
                if (column) {
                    keysChanged.push(column.key);
                    if (column.changes) {
                        keysChanged = keysChanged.concat(getFieldKeysByChanges(column.changes));
                    }
                }

                if (
                    column == undefined ||
                    (column.key != item.key &&
                        keysChanged.some((x) => {
                            return item.formula.indexOf(x) > -1;
                        }))
                ) {
                    const formulaMng = new FormulaManager(item.formula!);
                    formulaMng.Init(fields);
                    let coll = formulaMng.GetFields();
                    let values: any[] | undefined = undefined;
                    if (formMethods && row && coll.length > 0) {
                        values = GetFormulaValues(coll, table.key, fields, row, formMethods);
                    }
                    let val = await formulaMng.EvalFormulaValues(isEdit, isNew, values);
                    let valForm = isNaN(val) ? 0 : val;
                    if (item.floatPoints) {
                        valForm = round(valForm, item.floatPoints);
                    }
                    row[item.key] = +valForm;
                }
            }
        }

        return row;
    };

    const onChangeCellValue = async (
        row: any,
        oldRow: any,
        column: any,
        table: IDocumentTable,
        withOutUpdate?: boolean,
    ) => {
        if (column && column.key && table && table.key) {
            let val = row[column.key];
            await ChangesFieldValue(
                column.changes,
                val,
                table.key,
                fields,
                isEdit,
                isNew,
                withOutUpdate ? onSaveFieldWithOutUpdate : onSaveField,
                setError,
                table,
                row,
                formMethods,
                oldRow,
            );
        }
    };
    const onInitNewRow = async (data: any, table: IDocumentTable) => {
        const sets = table.newRowData?.setValues?.sets;
        if (table.newRowData && sets) {
            const promises = [];
            for (let index = 0; index < sets.length; index++) {
                const item = sets[index];
                promises.push(onSaveField(item, table, data, undefined, true));
            }
            await Promise.all(promises);
        }
    };

    const onInitCopyRow = async (data: any, table: IDocumentTable) => {
        const sets = table.copyRowData?.setValues?.sets;
        if (table.copyRowData && sets) {
            const promises = [];
            for (let index = 0; index < sets.length; index++) {
                const item = sets[index];
                promises.push(onSaveField(item, table, data, undefined, true));
            }
            await Promise.all(promises);
        }
    };

    const onInitSplitRow = async (data: any, table: IDocumentTable, indexRow: number) => {
        const sets = table.splitRows?.actions.setValues?.sets;
        const setsSelected = table.splitRows?.actions.setValuesSelected?.sets;
        if (table.splitRows && (sets || setsSelected)) {
            const promises = [];
            if ((indexRow != 0 || setsSelected == undefined) && sets) {
                for (let index = 0; index < sets.length; index++) {
                    const item = sets[index];
                    promises.push(onSaveField(item, table, data, undefined, true));
                }
            } else {
                if (setsSelected) {
                    for (let index = 0; index < setsSelected.length; index++) {
                        const item = setsSelected[index];
                        promises.push(onSaveField(item, table, data, undefined, true));
                    }
                }
            }
            await Promise.all(promises);
        }
    };

    const onValidateExternalRowsData = async (data: IDictionaryData[], table: IDocumentTable) => {
        let messages: string[] = [];
        let result: boolean = true;

        table.addFormDataRows?.validators.validators.forEach((validator) => {
            if (!checkGridRowsByScript(data, validator.script)) {
                messages.push(validator.message);
            }
        });

        if (messages.length > 0) {
            setError(messages);
            result = false;
        }

        return result;
    };

    const onSetFormDataNewRow = async (item: any, table: IDocumentTable, data: IDictionaryData) => {
        const sets = table.addFormDataRows?.setValues?.sets;
        if (table.addFormDataRows && sets) {
            const promises = [];
            for (let index = 0; index < sets.length; index++) {
                const set = sets[index];
                let elem: any = undefined;

                if (set.attr === 'code') {
                    elem = data.code;
                } else {
                    let elemField = data.fields.find((obj) => obj.name === set.attr);
                    if (elemField && elemField?.value !== undefined) {
                        elem = elemField.value;
                        elem = setValueTyped(elem, set);
                    }
                }

                if (elem && elem !== '') {
                    promises.push(onSaveField(set, table, item, elem));
                }
            }
            await Promise.all(promises);
        }
    };

    const onTableChange = async (value: any, table: IDocumentTable) => {
        await ChangesFieldValue(
            table.changes!,
            value,
            table.key,
            fields,
            isEdit,
            isNew,
            async (item: ISetValue, table?: IDocumentTable, rowData?: any, value?: any) => {
                await onSaveField(item, undefined, rowData, value);
            },
            setError,
            table,
            undefined,
            formMethods,
        );
        for (let index = 0; index < table?.tables?.length; index++) {
            const subTable = table.tables[index];
            await ChangesFieldValue(
                subTable.changes!,
                value,
                subTable.key,
                fields,
                isEdit,
                isNew,
                onSaveField,
                setError,
                subTable,
                undefined,
                formMethods,
            );
        }
    };

    const onTableRowDeleted = async (value: any, row: any, table: IDocumentTable) => {
        await ChangesFieldValue(
            table.onDeletedRows!,
            value,
            table.key,
            fields,
            isEdit,
            isNew,
            onSaveField,
            setError,
            table,
            row,
            formMethods,
        );
        for (let index = 0; index < table?.tables?.length; index++) {
            const subTable = table.tables[index];
            await ChangesFieldValue(
                subTable.onDeletedRows!,
                value,
                subTable.key,
                fields,
                isEdit,
                isNew,
                onSaveField,
                setError,
                subTable,
                row,
                formMethods,
            );
        }
    };

    const onTableRowCopied = async (value: any, row: any, table: IDocumentTable) => {
        await ChangesFieldValue(
            table.onCopiedRows!,
            value,
            table.key,
            fields,
            isEdit,
            isNew,
            onSaveField,
            setError,
            table,
            row,
            formMethods,
        );
        for (let index = 0; index < table.tables.length; index++) {
            const subTable = table.tables[index];
            await ChangesFieldValue(
                subTable.onCopiedRows!,
                value,
                subTable.key,
                fields,
                isEdit,
                isNew,
                onSaveField,
                setError,
                subTable,
                row,
                formMethods,
            );
        }
    };

    const onFormRowEdited = async (value: any, row: any, table: IDocumentTable) => {
        await ChangesFieldValue(
            table.onFormRowEdited!,
            value,
            table.key,
            fields,
            isEdit,
            isNew,
            onSaveField,
            setError,
            table,
            row,
            formMethods,
        );
        for (let index = 0; index < table.tables.length; index++) {
            const subTable = table.tables[index];
            await ChangesFieldValue(
                subTable.onFormRowEdited!,
                value,
                subTable.key,
                fields,
                isEdit,
                isNew,
                onSaveField,
                setError,
                subTable,
                row,
                formMethods,
            );
        }
    };

    const setParentField = (field: IField) => {
        formMethods.setValue(field.name as any, field.value as any, { shouldDirty: true });
    };

    const onFocusTextElement = useCallback((event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setTimeout(() => {
            const target = event.target;
            target.setSelectionRange(target.value.length, target.value.length);
        }, 0);
    }, []);

    return table && table.key && table.tableColumn && dataField ? (
        <TableControl
            name={dataField.name}
            formMethods={formMethods}
            table={table}
            docId={docId}
            fields={fields}
            getParentFields={getParentFields}
            setParentField={setParentField}
            allowUpdating={!table?.updateRowsDisabled}
            onTableRowDeleted={onTableRowDeleted}
            onTableRowCopied={onTableRowCopied}
            onFormRowEdited={onFormRowEdited}
            calculateRow={calculateRow}
            onChangeCellValue={onChangeCellValue}
            cellRenderSwitcher={cellRenderSwitcherField}
            cellTextValueSwitcher={cellTextValueSwitcher}
            editCellRenderSwitcher={editCellRenderSwitcher}
            evalTableFormulaValue={evalTableFormulaValue}
            onInitNewRow={onInitNewRow}
            onInitCopyRow={onInitCopyRow}
            onInitSplitRow={onInitSplitRow}
            getColumnWatches={getColumnWatches}
            getWatchesByFormula={getWatchesByFormula}
            onSaved={async (e: any) => {
                formMethods.setValue(dataField.name as any, e as any, { shouldDirty: true });
                await onTableChange(e, table);
            }}
            getFormValuesAsync={async () => {
                return await getFormValuesAsync(table?.addFormDataRows?.formValues!, null);
            }}
            getFiltersAsync={async () => {
                return await getFiltersAsync(table?.addFormDataRows?.filters!, null);
            }}
            onSetFormDataNewRow={onSetFormDataNewRow}
            onValidateExternalRowsData={onValidateExternalRowsData}
        />
    ) : (
        <></>
    );
};

export default EditTable;
